<template>
  <div class="step3-preview">
    <!-- 数据数量汇总展示 -->
    <div v-if="previewData" class="preview-summary">
      <div class="summary-item">
        <div class="label">预计生成子任务</div>
        <div class="value">{{ previewData.totalSubTasks }} <span class="unit">个</span></div>
      </div>
      <div class="summary-item">
        <div class="label">涉及设备总数</div>
        <div class="value">{{ previewData.totalDevices }} <span class="unit">台</span></div>
      </div>
      <div class="summary-item">
        <div class="label">涉及检查项总数</div>
        <div class="value">{{ previewData.totalCheckContents }} <span class="unit">项</span></div>
      </div>
    </div>

    <!-- 子任务表格详情 -->
    <div class="preview-table-wrapper">
      <el-table
        :data="Array.isArray(previewData && previewData.subTasks) ? previewData.subTasks : []"
        border
        stripe
        size="small"
        height="350"
      >
        <el-table-column type="expand" width="40">
          <template slot-scope="scope">
            <div v-if="scope.row.deviceTypeDetails && scope.row.deviceTypeDetails.length" class="type-detail-container">
              <div
                v-for="(typeDetail, idx) in scope.row.deviceTypeDetails"
                :key="idx"
                class="type-detail-block"
                :class="{ 'has-warning': typeDetail.deviceCount === 0 || typeDetail.checkContentCount === 0 }"
              >
                <div class="type-detail-header">
                  <span class="type-name">{{ typeDetail.deviceTypeName }}</span>
                  <el-tag size="mini" type="info">
                    设备 {{ typeDetail.deviceCount }} 台
                  </el-tag>
                  <el-tag size="mini" :type="typeDetail.checkContentCount > 0 ? 'success' : 'danger'">
                    检查项 {{ typeDetail.checkContentCount }} 项
                  </el-tag>
                  <el-tag v-if="typeDetail.deviceCount === 0" size="mini" type="danger">
                    <i class="el-icon-warning" /> 无匹配设备
                  </el-tag>
                  <el-tag v-if="typeDetail.checkContentCount === 0" size="mini" type="warning">
                    <i class="el-icon-warning" /> 无匹配检查内容
                  </el-tag>
                </div>
                <div class="type-detail-body">
                  <div class="detail-column">
                    <div class="detail-column-title">设备列表</div>
                    <template v-if="typeDetail.devices && typeDetail.devices.length">
                      <div
                        v-for="(d, di) in typeDetail.devices.slice(0, 6)"
                        :key="di"
                        class="detail-item"
                      >
                        <span class="device-code">{{ d.deviceCode }}</span>
                        <span class="device-name">{{ d.deviceName }}</span>
                      </div>
                      <div v-if="typeDetail.deviceCount > 6" class="detail-more">
                        ...还有 {{ typeDetail.deviceCount - 6 }} 台
                      </div>
                    </template>
                    <div v-else class="detail-empty">（无匹配设备）</div>
                  </div>
                  <div class="detail-column">
                    <div class="detail-column-title">检查内容</div>
                    <template v-if="typeDetail.checkContents && typeDetail.checkContents.length">
                      <div
                        v-for="(c, ci) in typeDetail.checkContents"
                        :key="ci"
                        class="detail-item"
                      >
                        {{ c.content }}
                      </div>
                    </template>
                    <div v-else class="detail-empty">（无匹配检查内容）</div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="taskName" label="任务名称（点击行首展开设备、检查项详情）" min-width="200" show-overflow-tooltip/>
        <el-table-column prop="periodName" label="周期" width="100" align="center"/>
        <el-table-column prop="systemName" label="消防系统" width="120" align="center"/>
        <el-table-column prop="deviceCount" label="设备数" width="90" align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.deviceCount > 0 && !scope.row.hasDeviceWarning" class="success-text">
              {{ scope.row.deviceCount }}
            </span>
            <el-tooltip
              v-else-if="scope.row.deviceCount > 0 && scope.row.hasDeviceWarning"
              content="部分设施类型未匹配到设备，请点击展开行查看详情并重新选择任务配置"
              placement="top"
            >
              <span class="warning-text" style="cursor:help">
                <i class="el-icon-warning" /> {{ scope.row.deviceCount }}
              </span>
            </el-tooltip>
            <el-tooltip v-else content="未匹配到任何设备，请点击展开行查看详情并重新选择任务配置" placement="top">
              <span class="danger-text" style="cursor:help"><i class="el-icon-warning" /> 0</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column prop="checkContentCount" label="检查项" width="90" align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.checkContentCount > 0 && !scope.row.hasCheckWarning" class="success-text">
              {{ scope.row.checkContentCount }}
            </span>
            <el-tooltip
              v-else-if="scope.row.checkContentCount > 0 && scope.row.hasCheckWarning"
              content="部分设施类型未匹配到检查内容，请点击展开行查看详情并重新选择任务配置"
              placement="top"
            >
              <span class="warning-text" style="cursor:help">
                <i class="el-icon-warning" /> {{ scope.row.checkContentCount }}
              </span>
            </el-tooltip>
            <el-tooltip v-else content="未匹配到任何检查内容，请点击展开行查看详情并重新选择任务配置" placement="top">
              <span class="danger-text" style="cursor: help;"><i class="el-icon-warning" /> 0</span>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <!-- 设备、检查内容校验提示 -->
    <div v-if="previewData && (!previewData.totalDevices || !previewData.totalSubTasks)" class="preview-hint preview-hint--error">
      <i class="el-icon-warning" /> 未匹配到任何设备，无法生成任务，请返回检查设施类型配置或设备台账。
    </div>
    <template v-else>
      <div v-if="hasDeviceTypeWarning" class="preview-hint preview-hint--error">
        <i class="el-icon-warning" /> 有任务涉及的设施类型未匹配到设备，请返回重新选择配置后再生成。
      </div>
      <div v-if="hasCheckTypeWarning" class="preview-hint preview-hint--error">
        <i class="el-icon-warning" /> 有任务涉及的设施类型未匹配到检查内容，请返回重新选择配置后再生成。
      </div>
    </template>
  </div>
</template>

<script>
/**
 * 第三步：展示预览接口返回的汇总与子任务表格，支持展开查看设施类型级设备与检查项。
 * 告警状态由本组件从 previewData 自行计算，通过 warning-change 事件通知父组件更新 canSubmit。
 */
export default {
  name: 'Step3Preview',
  props: {
    /** 预览结果：含 totalSubTasks、totalDevices、subTasks 等 */
    previewData: {
      type: Object,
      default: null
    }
  },
  computed: {
    /** 是否存在设施类型未匹配到设备的子任务 */
    hasDeviceTypeWarning () {
      return !!(this.previewData &&
        this.previewData.subTasks &&
        this.previewData.subTasks.some(t => t.hasDeviceWarning));
    },
    /** 是否存在设施类型未匹配到检查内容的子任务 */
    hasCheckTypeWarning () {
      return !!(this.previewData &&
        this.previewData.subTasks &&
        this.previewData.subTasks.some(t => t.hasCheckWarning));
    }
  },
  watch: {
    /** previewData 变化时通知父组件同步告警状态，用于 canSubmit 判断 */
    previewData () {
      this.$emit('warning-change', {
        hasDeviceTypeWarning: this.hasDeviceTypeWarning,
        hasCheckTypeWarning: this.hasCheckTypeWarning
      });
    }
  }
};
</script>

<style scoped lang="scss">
.preview-summary {
  display: flex;
  gap: 20px;
  margin-bottom: 10px;
  padding: 20px;
  background: #f0f9eb;
  border-radius: 4px;
  justify-content: center;

  .summary-item {
    text-align: center;
    min-width: 150px;

    .label { font-size: 14px; color: #67c23a; margin-bottom: 8px; }
    .value { font-size: 28px; font-weight: bold; color: #67c23a;
      .unit { font-size: 14px; font-weight: normal; margin-left: 4px; }
    }
  }
}

.preview-hint {
  margin-top: 5px;
  font-size: 13px;
  color: #E6A23C;
  background: #fdf6ec;
  padding: 8px;
  border-radius: 4px;

  &--error {
    color: #F56C6C;
    background: #fef0f0;
  }
}

.danger-text { color: #F56C6C; }
.success-text { color: #67C23A; font-weight: bold; }
.warning-text { color: #E6A23C; font-weight: bold; }

.type-detail-container {
  padding: 1px 1px;
}

.type-detail-block {
  margin-bottom: 10px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;

  &.has-warning {
    border-color: #faad14;
    background: #fffbe6;
  }

  .type-detail-header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: #f5f7fa;
    border-bottom: 1px solid #ebeef5;
    flex-wrap: wrap;

    .type-name {
      font-size: 13px;
      font-weight: bold;
      color: #303133;
      margin-right: 4px;
    }
  }

  .type-detail-body {
    display: flex;
    gap: 0;

    .detail-column {
      flex: 1;
      padding: 8px 10px;
      font-size: 12px;

      &:first-child {
        border-right: 1px dashed #ebeef5;
      }

      .detail-column-title {
        font-weight: bold;
        color: #606266;
        margin-bottom: 4px;
        padding-bottom: 4px;
        border-bottom: 1px solid #f0f0f0;
      }

      .detail-item {
        color: #303133;
        line-height: 1.8;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        .device-code {
          color: #909399;
          margin-right: 6px;
        }
      }

      .detail-more {
        color: #909399;
        font-style: italic;
        margin-top: 2px;
      }

      .detail-empty {
        color: #C0C4CC;
        font-style: italic;
      }
    }
  }
}
</style>
